/**
 * Launch plans (Oct 2026) — the ONE place the frontend describes plans.
 *
 * Prices here are a fallback only: the pricing page and register page load
 * live prices and limits from GET /subscription-plans/public and overlay
 * them. Admin subscription screens match plans by `backendEnum` against the
 * plan names returned by the API.
 *
 * Feature lists only promise what is built today (Oct 2026: HR, payroll,
 * CRM, collections and local-currency prices are live). Anything still in
 * development is marked "(coming soon)". Keep it that way for marketing.
 */

export type BillingCycle = "monthly" | "yearly";
export type PlanId = "start" | "growth" | "business" | "enterprise";

export interface PlanLimits {
  users: number | null;
  branches: number | null;
  businesses: number | null;
}

export interface LaunchPlan {
  id: PlanId;
  /** Display name. */
  name: string;
  /** Name stored on the backend plan document. */
  backendEnum: "Start" | "Growth" | "Business" | "Enterprise";
  monthly: number;
  yearly: number;
  /** 1 = lowest. Used for upgrade eligibility. */
  tier: number;
  blurb: string;
  features: string[];
  limits: PlanLimits;
  highlight?: boolean;
  isEnterprise?: boolean;
  /** Fixed price lists by currency from the API (GHS included). */
  prices?: Record<string, CyclePrices>;
}

export interface CyclePrices {
  monthly: number | null;
  yearly: number | null;
}

export const LAUNCH_PLANS: LaunchPlan[] = [
  {
    id: "start",
    name: "Start",
    backendEnum: "Start",
    monthly: 149,
    yearly: 1490,
    tier: 1,
    blurb: "For the single shop getting organised.",
    limits: { users: 3, branches: 1, businesses: 1 },
    features: [
      "Up to 3 users, 1 branch",
      "Point of sale with cash and mobile money",
      "Mobile money prompts at the till, paid out to your account",
      "Inventory with low-stock alerts",
      "Receipts and invoices",
      "Real double-entry accounting",
      "Daily sales and stock reports",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    backendEnum: "Growth",
    monthly: 299,
    yearly: 2990,
    tier: 2,
    highlight: true,
    blurb: "For growing businesses with a team or more than one branch.",
    limits: { users: 10, branches: 3, businesses: 1 },
    features: [
      "Up to 10 users, 3 branches",
      "Everything in Start, plus:",
      "Purchasing, suppliers and goods received",
      "Stock across branches and transfers",
      "Customer credit, receivables and loyalty",
      "P&L, cash flow and trial balance",
      "Customer profiles, follow-ups and membership",
      "HR & payroll as an add-on",
    ],
  },
  {
    id: "business",
    name: "Business",
    backendEnum: "Business",
    monthly: 599,
    yearly: 5990,
    tier: 3,
    blurb: "For established businesses that need control and depth.",
    limits: { users: 25, branches: 10, businesses: 1 },
    features: [
      "Up to 25 users, 10 branches",
      "Everything in Growth, plus:",
      "Trade with suppliers and buyers on Eaze",
      "Production and recipe costing",
      "Approvals and segregation of duties",
      "Full audit trail",
      "HR and payroll included",
      "Full CRM: segments, campaigns and automatic messages",
      "Priority support",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    backendEnum: "Enterprise",
    monthly: 0,
    yearly: 0,
    tier: 4,
    isEnterprise: true,
    blurb: "For groups running several businesses as one.",
    limits: { users: null, branches: null, businesses: null },
    features: [
      "Unlimited users, branches and businesses",
      "Group roll-up reporting",
      "Multi-currency, each business in its own money",
      "Your own agreement: pay by transfer, mobile money or card",
      "Dedicated onboarding and data migration",
      "Custom workflows and integrations",
      "Named account manager",
    ],
  },
];

/** Plans a customer can buy online (Enterprise goes through sales). */
export const SELF_SERVE_PLANS = LAUNCH_PLANS.filter((p) => !p.isEnterprise);

/** Old plan names (before Oct 2026) → launch plans. */
const LEGACY_NAMES: Record<string, PlanId> = {
  basic: "start",
  "basic eaze": "start",
  "basic-eaze": "start",
  standard: "growth",
  happy: "growth",
  "happy eaze": "growth",
  "happy-eaze": "growth",
  premium: "business",
  super: "business",
  "super eaze": "business",
  "super-eaze": "business",
};

export function planById(id: string | null | undefined): LaunchPlan | undefined {
  if (!id) return undefined;
  const key = String(id).toLowerCase().trim();
  const resolved = (LEGACY_NAMES[key] as string) || key;
  return LAUNCH_PLANS.find((p) => p.id === resolved);
}

/** Match a backend plan name (launch or legacy) to a launch plan. */
export function planByBackendName(name: string | null | undefined): LaunchPlan | undefined {
  if (!name) return undefined;
  const key = String(name).toLowerCase().trim();
  return (
    LAUNCH_PLANS.find((p) => p.backendEnum.toLowerCase() === key || p.name.toLowerCase() === key) ||
    planById(key)
  );
}

export function formatLimit(v: number | null | undefined): string {
  return v == null ? "Unlimited" : v.toLocaleString();
}

/** Shape returned by GET /subscription-plans/public. */
export interface PublicApiPlan {
  name: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  currency?: string;
  isFeatured?: boolean;
  limits?: Partial<PlanLimits>;
  prices?: Record<string, CyclePrices>;
}

/** Overlay live prices / limits from the API onto the static plans. */
export function mergeLivePlans(staticPlans: LaunchPlan[], api: PublicApiPlan[]): LaunchPlan[] {
  if (!Array.isArray(api) || api.length === 0) return staticPlans;
  return staticPlans.map((p) => {
    if (p.isEnterprise) return p;
    const m = api.find((a) => planByBackendName(a.name)?.id === p.id);
    if (!m) return p;
    return {
      ...p,
      monthly: typeof m.monthlyPrice === "number" ? m.monthlyPrice : p.monthly,
      yearly: typeof m.yearlyPrice === "number" ? m.yearlyPrice : p.yearly,
      highlight: typeof m.isFeatured === "boolean" ? m.isFeatured : p.highlight,
      limits: { ...p.limits, ...(m.limits || {}) } as PlanLimits,
      prices: m.prices || undefined,
    };
  });
}
